import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import type { CatalogProduct, ProductInput } from '@/api/catalogApi'
import { Field } from '@/components/common/Field'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import { FormError } from '@/components/admin/catalog/CatalogShared'
import { EMPTY_PRODUCT_FORM, keyFromName, productPayload, productToForm, validateProduct, type ProductForm as Form } from '@/lib/catalogForms'

/**
 * Product details: create (in the New product dialog) or edit (Details tab).
 * The key is locked once tickets reference the product.
 */
export function ProductForm({
  product,
  pending,
  error,
  onSubmit,
  onCancel,
}: {
  product?: CatalogProduct
  pending: boolean
  error: unknown
  onSubmit: (input: ProductInput) => void
  onCancel?: () => void
}) {
  const { t } = useTranslation()
  const [form, setForm] = useState<Form>(() => (product ? productToForm(product) : EMPTY_PRODUCT_FORM))
  const [keyTouched, setKeyTouched] = useState(!!product)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const err = (k: string) => (errors[k] ? t(errors[k]) : null)
  const keyLocked = !!product && product._count.tickets > 0
  const payload = productPayload(form, product)
  const dirty = !product || Object.keys(payload).length > 0

  function submit(e: FormEvent) {
    e.preventDefault()
    const found = validateProduct(form) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length === 0 && dirty) onSubmit(payload)
  }

  return (
    <form className="grid max-w-2xl grid-cols-2 gap-4" onSubmit={submit} noValidate>
      <Field id="pr-name" label={t('catalog.products.name')} error={err('name')}>
        <Input
          id="pr-name"
          value={form.name}
          onChange={(e) =>
            setForm({ ...form, name: e.target.value, ...(keyTouched ? {} : { key: keyFromName(e.target.value) }) })
          }
        />
      </Field>
      <Field
        id="pr-key"
        label={t('catalog.products.key')}
        error={err('key')}
        help={keyLocked ? t('catalog.products.keyLocked') : t('catalog.products.keyHelp')}
      >
        <Input
          id="pr-key"
          className="font-mono"
          value={form.key}
          disabled={keyLocked}
          onChange={(e) => {
            setKeyTouched(true)
            setForm({ ...form, key: e.target.value })
          }}
        />
      </Field>
      <Field id="pr-desc" label={t('catalog.products.description')} className="col-span-2">
        <Textarea id="pr-desc" rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
      </Field>
      <Field id="pr-sla" label={t('catalog.products.slaTarget')} error={err('slaTargetPct')}>
        <Input id="pr-sla" inputMode="numeric" value={form.slaTargetPct} onChange={(e) => setForm({ ...form, slaTargetPct: e.target.value })} />
      </Field>
      {product ? (
        <div className="col-span-2 flex flex-col gap-1">
          <label className="flex items-center gap-2 text-meta text-ink">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm({ ...form, active: e.target.checked })} />
            {t('catalog.products.active')}
          </label>
          <p className="text-label text-ink-muted">{t('catalog.products.inactiveNote')}</p>
        </div>
      ) : null}
      <FormError error={error} />
      <div className="col-span-2 flex justify-end gap-2">
        {onCancel ? (
          <Button type="button" variant="outline" onClick={onCancel}>
            {t('common.cancel')}
          </Button>
        ) : null}
        <Button type="submit" disabled={pending || !dirty}>
          {pending ? t('common.saving') : product ? t('common.save') : t('common.create')}
        </Button>
      </div>
    </form>
  )
}
